/**
 * CUSTOMER-R4.7 — presentation-only timeline collapse.
 * Does not rewrite DB history. Groups identical-time duplicate customer labels.
 */

export type PresentableTimelineEvent = {
  status: string;
  note: string | null;
  created_at: string;
  title_uk?: string;
  explanation_uk?: string;
  shipment_number?: number | null;
  receipt_link?: string | null;
  active?: boolean;
  event_type?: string;
};

function normTitle(ev: PresentableTimelineEvent): string {
  return String(ev.title_uk ?? ev.status ?? "")
    .trim()
    .toLowerCase();
}

function eventMs(raw: string | null | undefined): number {
  const t = Date.parse(String(raw ?? ""));
  return Number.isFinite(t) ? t : 0;
}

const REDUNDANT_WITH_PAID = new Set([
  "готово до відправлення",
  "замовлення прийнято",
  "нове замовлення",
]);

function isPaymentConfirmedLabel(title: string): boolean {
  return title.includes("оплату підтверджено") || title.includes("кошті отримано") || title === "оплачено";
}

/**
 * Collapse consecutive identical labels within 2s, and drop implementation-noise
 * labels that share a timestamp with a bank-paid confirmation.
 */
export function presentCustomerTimeline(
  events: PresentableTimelineEvent[],
): PresentableTimelineEvent[] {
  if (!Array.isArray(events) || events.length === 0) return [];
  const out: PresentableTimelineEvent[] = [];
  for (const ev of events) {
    const title = normTitle(ev);
    const prev = out[out.length - 1];
    if (prev && normTitle(prev) === title && Math.abs(eventMs(ev.created_at) - eventMs(prev.created_at)) <= 2000) {
      if (ev.active === true) prev.active = true;
      continue;
    }
    out.push({ ...ev });
  }

  const paidTimes = out.filter((ev) => isPaymentConfirmedLabel(normTitle(ev))).map((ev) => eventMs(ev.created_at));
  return out.filter((ev) => {
    const title = normTitle(ev);
    if (!REDUNDANT_WITH_PAID.has(title)) return true;
    return !paidTimes.some((t) => Math.abs(eventMs(ev.created_at) - t) <= 2000);
  });
}
